import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import {
  Avatar,
  GridLayout,
  GridLayoutItem,
} from "@progress/kendo-react-layout";
import { PagerTargetEvent } from "@progress/kendo-react-data-tools";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { FiEdit } from "react-icons/fi";
import { Switch } from "@progress/kendo-react-inputs";
import AlertBox from "../../components/common/AlertBox";
import { LoadingPanel } from "../../components/layout/Loading";
import IconButton from "../../components/common/IconButton";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { MdDelete } from "react-icons/md";

import { checkAcessRights } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
} from "../../_contstants/common";
import {
  ActiveInactiveBanner,
  deleteBanner,
  getAllBanner,
} from "./services/banner.services";
import { clearBannerID, setBannerID } from "./bannerSlice";

const Banner: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);

  useEffect(() => {
    dispatch(getAllBanner());
    return () => {
      dispatch(closeDialog());
    };
  }, []);

  const handleCreate = () => {
    navigate("/banner/create");
  };

  return (
    <>
      <DeleteBannerDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 0 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Banner List
          </Typography.h4>
          {isAddAccess && (
            <Button
              onClick={handleCreate}
              fillMode={"solid"}
              themeColor={"primary"}
            >
              {"Create Banner"}
            </Button>
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <BannerGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const BannerGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);
  const BannerList = useAppSelector((state) => state.banner.BannerList);
  const loading = useAppSelector((state) => state.banner.loading);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();
  const [expandedRow, setExpandedRow] = useState(null);
  const truncateDescription = (Description: string) => {
    if (!Description) return "";
    const maxLength = 100;
    if (Description.length <= maxLength) return Description;
    return Description.substring(0, maxLength) + "...";
  };
  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? BannerList.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const MyEditCommandCell = (props: GridCellProps) => {
    const handleEdit = (ID: number) => {
      navigate("/banner/edit", { state: { BannerID: ID } });
    };
    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteBanner"));
      dispatch(setBannerID(ID));
    };

    return (
      <td
        className={`action-td ${props.className}`}
        style={{
          ...props.style,
        }}
      >
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            onClick={() => handleEdit(props.dataItem.ID)}
          >
            <FiEdit
              className="absolute-position"
              style={{ fontSize: "20px" }}
            />
          </IconButton>
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            themeColor="error"
            onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
          >
            <MdDelete
              className="absolute-position"
              style={{ fontSize: "24px" }}
            />
          </IconButton>
        )}
      </td>
    );
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };
  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${
        props.index === 6 || props.index === 5 ? "locked-header" : ""
      }`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const MyActiveInActtiveCommandCell = (props: GridCellProps) => {
    const [IsActive, setIsActive] = useState<boolean>(
      props.dataItem.IsActive ?? false
    );

    const handleActiveInActive = () => {
      setIsActive(!IsActive);
      const data = {
        ID: props.dataItem.ID,
        IsActive: !IsActive,
      };
      dispatch(ActiveInactiveBanner(data)).then(() => {
        dispatch(getAllBanner());
        // if (response.payload?.data?.Status === 200) {
        //   dispatch(getAllBankes());
        // } else {
        //   dispatch(getAllBankes());
        // }
      });
    };

    return (
      <td
        style={{ textAlign: "center", ...props.style }}
        className={props.className}
      >
        <Switch
          disabled={isEditAccess ? false : true}
          name="IsActive"
          checked={IsActive}
          onChange={handleActiveInActive}
          size="small"
        />
      </td>
    );
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      {BannerList && BannerList.length ? (
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 152px)" }}
          filterable={true}
          className="responsive-table"
          filter={filter}
          data={
            filter
              ? filterBy(BannerList, filter).slice(
                  page.skip,
                  page.take + page.skip
                )
              : BannerList.slice(page.skip, page.take + page.skip)
          }
          onFilterChange={handleFilterChange}
          skip={page.skip}
          take={page.take}
          total={BannerList.length}
          pageable={{
            buttonCount: 5,
            pageSizes: [5, 10, 15, "All"],
            pageSizeValue: pageSizeValue,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column
            field="BannerTitle"
            title="Banner Title"
            width={200}
            cell={(props: any) => (
              <td>
                {isEditAccess ? (
                  <div
                    className="hoverableName"
                    onClick={() =>
                      navigate("/banner/edit", {
                        state: { BannerID: props.dataItem?.ID },
                      })
                    }
                  >
                    {props.dataItem?.BannerTitle}
                  </div>
                ) : (
                  <div className="normalName">
                    {props.dataItem?.BannerTitle}
                  </div>
                )}
              </td>
            )}
          />

          <Column field="BannerType" title="Banner Type" width={150} />
          <Column field="SubTitle" title="Sub Title" width={200} />
          {/* <Column field="Description" title="Description" /> */}
          <Column
            field="Description"
            title="Description"
            width={"300px"}
            cell={(props: any) => (
              <td>
                <div
                  className="remarks-content"
                  dangerouslySetInnerHTML={{
                    __html:
                      expandedRow === props.dataItem?.ID
                        ? props.dataItem?.Description
                        : truncateDescription(props.dataItem?.Description),
                  }}
                />
                {props.dataItem?.Description?.length > 100 && (
                  <span
                    className="readmore-btn"
                    style={{
                      textAlign: "right",
                    }}
                    onClick={() =>
                      setExpandedRow(
                        expandedRow === props.dataItem?.ID
                          ? null
                          : props.dataItem?.ID
                      )
                    }
                  >
                    <u>
                      {expandedRow === props.dataItem?.ID
                        ? "Read Less"
                        : "Read More"}
                    </u>
                  </span>
                )}
              </td>
            )}
          />
          <Column
            field="BannerImage"
            title="Image"
            width={100}
            filterable={false}
            cell={(props: any) => (
              <td className="text-center">
                <Avatar
                  style={{
                    width: 45,
                    height: 45,
                    flexBasis: 45,
                  }}
                  rounded={"full"}
                  type={"image"}
                >
                  <img
                    src={props.dataItem?.BannerImage}
                    alt="Inner"
                    onClick={() => window.open(props.dataItem?.BannerImage)}
                    width={200}
                    height={200}
                  />
                </Avatar>
              </td>
            )}
          />

          <Column
            field="ID"
            title="Status"
            cell={MyActiveInActtiveCommandCell}
            width={"90px"}
            filterable={false}
            locked={true}
          />
          {(isEditAccess || isDeleteAccess) && (
            <Column
              field="ID"
              title="Actions"
              cell={MyEditCommandCell}
              width={"110px"}
              locked={true}
              filterable={false}
            />
          )}
        </KendoGrid>
      ) : (
        <AlertBox />
      )}
    </>
  );
};

const DeleteBannerDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const BannerID = useAppSelector((state) => state.banner.BannerID);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteBanner(ID));
      if (response.payload.Data.Status === 200) {
        dispatch(getAllBanner());
        dispatch(closeDialog());
        dispatch(clearBannerID());
      } else {
        dispatch(getAllBanner());
        dispatch(closeDialog());
        dispatch(clearBannerID());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteBanner" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Banner"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Banner ?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(BannerID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default Banner;
